'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { MessageItem } from './MessageItem';
import { FileUploadButton } from './FileUploadButton';
import { AttachmentPreview } from './AttachmentPreview';
import { cn } from '@/src/shared/lib/utils';
import { useChatStore } from '../model/useChatStore';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import { chatSocketService } from '@/src/shared/services/chat-socket.service';
import { useModal } from '@/src/shared/hooks/useModal';
import { buildApiUrl } from '@/src/shared/api/config';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';
import { ordersApi } from '@/src/shared/api';
import type { Message, OrderStatus } from '../types';

interface ChatMessagesProps {
  conversationId: string | null;
  onBack?: () => void;
  className?: string;
}

/**
 * Get date separator label
 */
function getDateLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (messageDate.getTime() === today.getTime()) {
    return 'Today';
  }
  if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: messageDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Group messages by date
 */
function groupMessagesByDate(messages: Message[]): { date: string; messages: Message[] }[] {
  const groups: { date: string; messages: Message[] }[] = [];
  let currentDate = '';

  for (const message of messages) {
    const messageDate = getDateLabel(new Date(message.createdAt));
    if (messageDate !== currentDate) {
      currentDate = messageDate;
      groups.push({ date: messageDate, messages: [message] });
    } else {
      groups[groups.length - 1].messages.push(message);
    }
  }

  return groups;
}

/**
 * Get status badge config
 */
function getOrderStatusConfig(status: OrderStatus): {
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
} {
  switch (status) {
    case 'ASSIGNED':
    case 'IN_PROGRESS':
      return {
        label: 'In Progress',
        bgColor: 'bg-transparent',
        textColor: 'text-indigo-700',
        borderColor: 'border-indigo-700',
      };
    case 'PENDING_REVIEW':
      return {
        label: 'Pending Review',
        bgColor: 'bg-transparent',
        textColor: 'text-orange-600',
        borderColor: 'border-orange-600',
      };
    case 'COMPLETED':
      return {
        label: 'Completed',
        bgColor: 'bg-transparent',
        textColor: 'text-green-600',
        borderColor: 'border-green-600',
      };
    default:
      return {
        label: status,
        bgColor: 'bg-transparent',
        textColor: 'text-gray-600',
        borderColor: 'border-gray-400',
      };
  }
}

export const ChatMessages = ({
  conversationId,
  onBack,
  className,
}: ChatMessagesProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, number>>(
    new Map()
  );
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(0);

  // Get active actor from auth store
  const activeActor = useAuthStore((state) => state.activeActor);

  // Modal for creating orders
  const { open: openPostTaskModal } = useModal('post-task');

  const {
    conversations,
    getMessages,
    loadMessages,
    sendMessage,
    markAsRead,
    joinConversation,
    isLoadingMessages,
    getTypingActors,
    connectionStatus,
  } = useChatStore();

  const messages = conversationId ? getMessages(conversationId) : [];
  const isLoading = conversationId ? isLoadingMessages[conversationId] : false;
  const typingActors = conversationId ? getTypingActors(conversationId) : [];

  // Find current conversation
  const conversation = conversations.find((c) => c.id === conversationId);

  // Group messages by date (no virtual ORDER_CARD anymore, rely on real messages from backend)
  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages]);

  // Determine target actor (the other participant) for creating direct orders
  const getTargetActor = () => {
    if (!conversation || !activeActor) return null;

    // Use otherParticipant which contains the other party's actor info
    const otherParticipant = conversation.otherParticipant;
    if (!otherParticipant) return null;

    return {
      id: otherParticipant.id,
      name: otherParticipant.displayName || otherParticipant.name || 'User',
      type: activeActor.type === 'CLIENT' ? 'WORKER' : 'CLIENT',
    };
  };

  // Handle create order button click
  const handleCreateOrder = () => {
    const targetActor = getTargetActor();
    if (!targetActor || !activeActor) return;

    openPostTaskModal({
      orderType: 'DIRECT',
      targetActorId: targetActor.id,
      targetActorName: targetActor.name,
      currentActorType: activeActor.type,
    });
  };

  // Load messages when conversation is selected
  useEffect(() => {
    if (conversationId) {
      // Load messages
      loadMessages(conversationId);
    }
  }, [conversationId, loadMessages]);

  // Join conversation room when connected
  useEffect(() => {
    if (conversationId && connectionStatus === 'connected') {
      // Add small delay to ensure socket is fully ready
      const timer = setTimeout(() => {
        joinConversation(conversationId).catch((err) => {
          console.error('[ChatMessages] Failed to join conversation:', err);
          // Retry once after 1 second if socket disconnected during join
          if (err.message === 'Socket not connected') {
            setTimeout(() => {
              if (connectionStatus === 'connected') {
                joinConversation(conversationId).catch(console.error);
              }
            }, 1000);
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [conversationId, connectionStatus, joinConversation]);

  // Initial scroll to bottom when chat is first opened
  useEffect(() => {
    if (!messagesContainerRef.current || isLoading || messages.length === 0) return;

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'instant',
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isLoading, conversationId]); // Only run when conversation changes or loading finishes

  // Smart auto-scroll: only scroll to bottom if user is near bottom OR sent message themselves
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    const container = messagesContainerRef.current;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;

    // Check if new messages were added
    const newMessagesAdded = messages.length > prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;

    if (!newMessagesAdded) return;

    // Get last message
    const lastMessage = messages[messages.length - 1];
    const isSentByMe = lastMessage?.senderActorId === activeActor?.id;

    // Auto-scroll if:
    // 1. User sent the message themselves, OR
    // 2. User is already near the bottom (reading latest messages)
    if (isSentByMe || isNearBottom) {
      // Scroll the container, not the page
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, activeActor]);

  // Mark messages as read when visible
  useEffect(() => {
    if (!conversationId || messages.length === 0 || !activeActor) return;

    // Find unread messages from other participants
    const unreadMessages = messages.filter(
      (msg) => msg.senderActorId !== activeActor.id
    );

    if (unreadMessages.length > 0) {
      const lastMessage = unreadMessages[unreadMessages.length - 1];
      markAsRead(conversationId, lastMessage.id);
    }
  }, [conversationId, messages, markAsRead, activeActor]);

  // Handle typing indicator
  const handleTyping = () => {
    if (conversationId && inputValue.trim()) {
      chatSocketService.typing(conversationId);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (conversationId && !inputValue.trim()) {
        chatSocketService.stopTyping(conversationId);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [inputValue, conversationId]);

  // Handle file selection
  const handleFilesSelected = (files: File[]) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  // Upload file
  const uploadFile = async (file: File): Promise<string | null> => {
    const fileId = `${file.name}-${Date.now()}`;

    try {
      setUploadingFiles((prev) => new Map(prev).set(fileId, 0));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversationId', conversationId!);

      const response = await fetchWithTokenRefresh(buildApiUrl('/chat/attachments'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[ChatMessages] Upload error response:', errorData);
        // API returns nested message structure: { message: { message: "..." } }
        const errorMessage =
          typeof errorData.message === 'string'
            ? errorData.message
            : errorData.message?.message || 'Upload failed';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const attachment = data.data;

      setUploadingFiles((prev) => {
        const next = new Map(prev);
        next.delete(fileId);
        return next;
      });

      return attachment.id;
    } catch (error) {
      console.error('Upload error:', error);
      setUploadingFiles((prev) => {
        const next = new Map(prev);
        next.delete(fileId);
        return next;
      });
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to upload file. Please try again.'
      );
      return null;
    }
  };

  // Handle send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!conversationId || isSending) return;

    const body = inputValue.trim();

    // Upload pending files first
    let attachmentIds: string[] | undefined;
    if (pendingFiles.length > 0) {
      setIsSending(true);

      const uploadPromises = pendingFiles.map(uploadFile);
      const uploadedIds = await Promise.all(uploadPromises);
      const validIds = uploadedIds.filter((id) => id !== null) as string[];

      if (validIds.length !== pendingFiles.length) {
        alert('Some files failed to upload');
      }

      attachmentIds = validIds.length > 0 ? validIds : undefined;
      setPendingFiles([]);
      setIsSending(false);
    }

    // Need at least body or attachments
    if (!body && (!attachmentIds || attachmentIds.length === 0)) {
      return;
    }

    // Validate length
    if (body.length > 2000) {
      alert('Message is too long (max 2000 characters)');
      return;
    }

    setIsSending(true);
    setInputValue('');

    try {
      await sendMessage(conversationId, body, attachmentIds);

      // Stop typing indicator
      if (chatSocketService.isConnected()) {
        chatSocketService.stopTyping(conversationId);
      }
    } catch (error) {
      console.error('Failed to send message:', error);

      // Restore input
      setInputValue(body);

      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Order actions state
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const order = conversation?.activeOrder;
  const orderStatus = order?.status;
  const isWorker = activeActor?.type === 'WORKER';
  const isClient = activeActor?.type === 'CLIENT';

  // Debug: Log order state
  console.log('[ChatMessages] Has order?', !!order);
  console.log('[ChatMessages] Order:', order);
  console.log('[ChatMessages] Show Create Order button?', !order);

  // Handle unclaim order
  const handleUnclaim = async () => {
    if (!order || isUpdatingOrder) return;
    if (!confirm('Are you sure you want to unclaim this order?')) return;

    setIsUpdatingOrder(true);
    try {
      await ordersApi.updateStatus(order.id, 'PUBLISHED');
      // Refresh will happen via websocket or next load
    } catch (error) {
      console.error('Failed to unclaim:', error);
      alert('Failed to unclaim order.');
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  // Handle mark as done
  const handleMarkDone = async () => {
    if (!order || isUpdatingOrder) return;
    if (!confirm('Mark this order as complete?')) return;

    setIsUpdatingOrder(true);
    try {
      await ordersApi.updateStatus(order.id, 'PENDING_REVIEW');
    } catch (error) {
      console.error('Failed to mark as done:', error);
      alert('Failed to mark order as done.');
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  if (!conversationId) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-text-secondary text-base">
          Select a conversation to view messages
        </span>
      </div>
    );
  }

  // Get participant info for header
  const participantName =
    conversation?.otherParticipant?.displayName ||
    conversation?.otherParticipant?.name ||
    'User';

  // Get order status config for badge
  const statusConfig = orderStatus ? getOrderStatusConfig(orderStatus) : null;

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Conversation Header - New Design */}
      {conversation && (
        <div className="flex items-center justify-between px-5 py-2 border-b border-accent-secondary bg-background-primary">
          {/* Left side: Back arrow + Name */}
          <div className="flex items-center gap-2">
            {/* Back Arrow */}
            {onBack && (
              <button
                onClick={onBack}
                className="p-0 hover:opacity-70 transition-opacity"
                aria-label="Back"
              >
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <path
                    d="M30 36L18 24L30 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-text-primary"
                  />
                </svg>
              </button>
            )}

            {/* Participant Name */}
            <span className="text-text-primary text-2xl font-semibold font-['Roboto'] leading-8">
              {participantName}
            </span>
          </div>

          {/* Right side: Status badge + Action buttons */}
          <div className="flex items-center gap-2">
            {/* Order Status Badge */}
            {statusConfig && (
              <div
                className={cn(
                  'h-8 px-5 py-3 rounded-[10px] border',
                  'inline-flex justify-center items-center gap-2',
                  statusConfig.bgColor,
                  statusConfig.borderColor
                )}
              >
                <span
                  className={cn(
                    'text-base font-extrabold font-[\'Roboto\'] leading-5',
                    statusConfig.textColor
                  )}
                >
                  {statusConfig.label}
                </span>
              </div>
            )}

            {/* Unclaim Button - for workers on in-progress orders */}
            {isWorker && (orderStatus === 'ASSIGNED' || orderStatus === 'IN_PROGRESS') && (
              <button
                onClick={handleUnclaim}
                disabled={isUpdatingOrder}
                className={cn(
                  'h-10 px-5 py-3 rounded-[750px]',
                  'border border-feedback-attention',
                  'inline-flex justify-center items-center gap-2',
                  'hover:bg-feedback-attention/10 transition-colors',
                  isUpdatingOrder && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="text-feedback-attention text-xl font-medium font-['Roboto'] leading-6">
                  Unclaim
                </span>
              </button>
            )}

            {/* Mark as Done Button - for workers on in-progress orders */}
            {isWorker && orderStatus === 'IN_PROGRESS' && (
              <button
                onClick={handleMarkDone}
                disabled={isUpdatingOrder}
                className={cn(
                  'h-10 px-5 py-3 rounded-[750px]',
                  'border border-accent-secondary',
                  'inline-flex justify-center items-center gap-2',
                  'hover:bg-accent-secondary/10 transition-colors',
                  isUpdatingOrder && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="text-accent-primary text-xl font-medium font-['Roboto'] leading-6">
                  Mark as done
                </span>
              </button>
            )}

            {/* Create Order Button - shown when no active order */}
            {!order && (
              <button
                onClick={handleCreateOrder}
                className={cn(
                  'h-10 px-5 py-3 rounded-[750px]',
                  'bg-accent-primary text-white',
                  'inline-flex justify-center items-center gap-2',
                  'hover:bg-accent-primary/90 transition-colors'
                )}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span className="text-xl font-medium font-['Roboto'] leading-6">
                  Create Order
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="text-text-secondary">Loading messages...</div>
        </div>
      )}

      {/* Messages List with Date Separators */}
      {!isLoading && (
        <>
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto py-4">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full p-8">
                <div className="text-center text-text-secondary">
                  <p>No messages yet</p>
                  <p className="text-sm mt-2">
                    Send a message to start the conversation
                  </p>
                </div>
              </div>
            ) : (
              <>
                {messageGroups.map((group) => (
                  <div key={group.date}>
                    {/* Date Separator */}
                    <div className="flex justify-center py-4">
                      <div className="h-8 px-7 rounded-full border border-accent-secondary inline-flex justify-center items-center">
                        <span className="text-text-primary text-base font-normal font-['Roboto'] leading-5">
                          {group.date}
                        </span>
                      </div>
                    </div>
                    {/* Messages for this date */}
                    {group.messages.map((message) => (
                      <MessageItem key={message.id} message={message} />
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Typing Indicator */}
          {typingActors.length > 0 && (
            <div className="px-5 py-2 text-sm text-text-secondary italic">
              {typingActors.length === 1
                ? 'Typing...'
                : `${typingActors.length} people are typing...`}
            </div>
          )}
        </>
      )}

      {/* Input Area - New Design */}
      <div className="py-5 px-5">
        <form onSubmit={handleSendMessage} className="flex items-end gap-5">
          {/* Input Container */}
          <div className="flex-1 flex flex-col gap-[5px]">
            {/* Attachments preview */}
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 px-3.5">
                {pendingFiles.map((file, index) => (
                  <AttachmentPreview
                    key={`${file.name}-${index}`}
                    file={file}
                    onRemove={() => {
                      setPendingFiles((prev) => prev.filter((_, i) => i !== index));
                    }}
                  />
                ))}
              </div>
            )}

            {/* Input Field with Clip Icon */}
            <div
              className={cn(
                'h-14 p-3.5 bg-background-primary rounded-[20px]',
                'shadow-[inset_0px_-1px_10px_0px_rgba(42,42,15,0.10)]',
                'border border-background-secondary',
                'flex items-center gap-2.5'
              )}
            >
              {/* Text Input */}
              <div className="flex-1 flex items-center overflow-hidden">
                <textarea
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    handleTyping();
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  className={cn(
                    'flex-1 bg-transparent',
                    'text-text-primary text-base font-normal font-[\'Roboto\'] leading-5',
                    'border-0 outline-none resize-none',
                    'placeholder:text-text-secondary',
                    'max-h-[88px] overflow-y-auto'
                  )}
                  rows={1}
                  disabled={isSending}
                  maxLength={2000}
                  style={{ height: 'auto', minHeight: '24px' }}
                />
              </div>

              {/* Clip/Attachment Button */}
              <div className="w-12 h-12 rounded-full shadow-[0px_4px_15px_0px_rgba(0,0,0,0.15)] flex items-center justify-center flex-shrink-0 bg-background-primary">
                <FileUploadButton
                  onFilesSelected={handleFilesSelected}
                  disabled={isSending}
                  maxFiles={10}
                  maxSizeMB={5}
                  variant="icon"
                />
              </div>
            </div>
          </div>

          {/* Send Button - Circular */}
          <button
            type="submit"
            disabled={(!inputValue.trim() && pendingFiles.length === 0) || isSending}
            className={cn(
              'w-24 h-14 rounded-full flex-shrink-0',
              'flex justify-center items-center gap-2.5',
              'transition-colors',
              (inputValue.trim() || pendingFiles.length > 0) && !isSending
                ? 'bg-accent-primary hover:bg-accent-primary/90'
                : 'bg-gray-300 cursor-not-allowed'
            )}
          >
            {isSending ? (
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M28 4L14 18M28 4L19 28L14 18M28 4L4 13L14 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
